import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowDownWideNarrow,
  ArrowRight,
  Grid2X2,
  List as ListIcon,
  SlidersHorizontal,
  X,
} from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { getErrorMessage } from "../../core/api.jsx";
import { getCategories, getProducts } from "../../core/services/catalogService";
import ProductCard from "../../shared/ProductCard";

export default function ProductList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("keyword") || "";
  const category = searchParams.get("category") || "";
  const subCategory = searchParams.get("subCategory") || "";
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [sort, setSort] = useState(searchParams.get("sort") || "featured");
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [viewMode, setViewMode] = useState(
    () => localStorage.getItem("catalogViewMode") || "grid",
  );
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  useEffect(() => {
    setPage(1);
    setSort(searchParams.get("sort") || "featured");
  }, [keyword, category, subCategory, searchParams]);

  useEffect(() => {
    let alive = true;
    getCategories()
      .then((data) => {
        if (alive) setCategories(data.categories || []);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    const params = new URLSearchParams({ page: String(page) });
    if (keyword) params.set("keyword", keyword);
    if (category) params.set("category", category);
    if (subCategory) params.set("subCategory", subCategory);
    getProducts(params.toString())
      .then((data) => {
        if (!alive) return;
        setProducts(data.products || []);
        setCount(Number(data.productsCount || 0));
      })
      .catch((requestError) => {
        if (alive)
          setError(getErrorMessage(requestError, "We couldn't load products."));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [page, keyword, category, subCategory]);

  const visibleProducts = useMemo(() => {
    const list = [...products];
    if (sort === "price-low")
      return list.sort(
        (a, b) =>
          Number(a.sellingPrice || a.price) - Number(b.sellingPrice || b.price),
      );
    if (sort === "price-high")
      return list.sort(
        (a, b) =>
          Number(b.sellingPrice || b.price) - Number(a.sellingPrice || a.price),
      );
    if (sort === "new")
      return list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    return list;
  }, [products, sort]);

  const chooseCategory = (name) => {
    const next = new URLSearchParams(searchParams);
    if (name) next.set("category", name);
    else next.delete("category");
    next.delete("subCategory");
    setSearchParams(next);
  };

  const chooseSubCategory = (name) => {
    const next = new URLSearchParams(searchParams);
    if (name) next.set("subCategory", name);
    else next.delete("subCategory");
    setSearchParams(next);
  };

  const changeView = (mode) => {
    setViewMode(mode);
    localStorage.setItem("catalogViewMode", mode);
  };

  const selectedCategory = categories.find((item) => item.name === category);
  const subCategories = selectedCategory?.subCategories || [];

  return (
    <main className="page-shell shop-page">
      <div className="breadcrumbs">
        <Link to="/">Home</Link>
        <span>/</span>
        <span>Shop</span>
        {category && (
          <>
            <span>/</span>
            <span>{category}</span>
          </>
        )}
        {subCategory && (
          <>
            <span>/</span>
            <span>{subCategory}</span>
          </>
        )}
      </div>
      <section className="shop-intro shop-intro--catalog">
        <div>
          <p className="eyebrow">SUGAM STORE · CATALOG</p>
          <h1>
            {keyword ? (
              <>
                Search results for <em>“{keyword}”</em>
              </>
            ) : (
              subCategory || (category ? category : "All Products & Collections")
            )}
          </h1>
          <p>{keyword ? `${count} products found` : `${count} products to explore`}</p>
        </div>
      </section>
      <div className="shop-mobile-categories" aria-label="Quick categories">
        <button className={!category ? "is-selected" : ""} onClick={() => chooseCategory("")}>All</button>
        {categories.map((item) => (
          <button className={category === item.name ? "is-selected" : ""} key={item._id} onClick={() => chooseCategory(item.name)}>
            {item.name}
          </button>
        ))}
      </div>
      <div className="shop-layout">
        <aside className={`shop-sidebar${mobileFiltersOpen ? " is-open" : ""}`}>
          <div className="shop-sidebar__heading">
            <h2>Filters</h2>
            <button className="shop-sidebar__close" onClick={() => setMobileFiltersOpen(false)} aria-label="Close filters"><X size={18} /></button>
          </div>
          <section className="shop-filter-section">
            <h3>Categories</h3>
            <button className={`shop-filter-option${!category ? " is-selected" : ""}`} onClick={() => chooseCategory("")}>
              <span className="shop-filter-checkbox" />All categories
            </button>
            {categories.map((item) => (
              <button className={`shop-filter-option${category === item.name ? " is-selected" : ""}`} key={item._id} onClick={() => chooseCategory(item.name)}>
                <span className="shop-filter-checkbox" />{item.name}
              </button>
            ))}
          </section>
          {category && subCategories.length > 0 && (
            <section className="shop-filter-section">
              <h3>Subcategories</h3>
              <button className={`shop-filter-option${!subCategory ? " is-selected" : ""}`} onClick={() => chooseSubCategory("")}>
                <span className="shop-filter-checkbox" />All {category}
              </button>
              {subCategories.map((item) => (
                <button className={`shop-filter-option${subCategory === item.name ? " is-selected" : ""}`} key={item._id} onClick={() => chooseSubCategory(item.name)}>
                  <span className="shop-filter-checkbox" />{item.name}
                </button>
              ))}
            </section>
          )}
          <button className="shop-sidebar__clear" onClick={() => setSearchParams({})}>Clear all filters</button>
        </aside>
        <section className="shop-results">
          <div className="shop-results__toolbar">
            <div className="shop-results__mobile-actions">
              <button className="shop-filter-toggle" onClick={() => setMobileFiltersOpen((open) => !open)}>
                <SlidersHorizontal size={16} /> Filters
              </button>
            </div>
            <div className="shop-results__active-filters">
              {category && <button onClick={() => chooseCategory("")}>{category}<X size={14} /></button>}
              {subCategory && <button onClick={() => chooseSubCategory("")}>{subCategory}<X size={14} /></button>}
              {!category && !subCategory && <span>Showing {count} products</span>}
            </div>
            <div className="shop-results__controls">
              <label className="sort-select">
                <ArrowDownWideNarrow size={16} />
                <span>Sort by</span>
                <select aria-label="Sort products" value={sort} onChange={(event) => setSort(event.target.value)}>
                  <option value="featured">Popular</option>
                  <option value="new">Newest</option>
                  <option value="price-low">Price: low to high</option>
                  <option value="price-high">Price: high to low</option>
                </select>
              </label>
              <div className="shop-view-toggle" aria-label="Product layout">
                <button className={viewMode === "grid" ? "is-active" : ""} onClick={() => changeView("grid")} aria-label="Grid view" aria-pressed={viewMode === "grid"}><Grid2X2 size={17} /></button>
                <button className={viewMode === "list" ? "is-active" : ""} onClick={() => changeView("list")} aria-label="List view" aria-pressed={viewMode === "list"}><ListIcon size={19} /></button>
              </div>
            </div>
          </div>
          {error && (
            <div className="inline-alert">
              <span>{error}</span>
              <button className="text-button" onClick={() => setPage((current) => current)}>Try again</button>
            </div>
          )}
          {!error && loading && (
            <div className={`product-grid product-grid--shop is-${viewMode}`}>
              {[0, 1, 2, 3, 4, 5, 6, 7].map((key) => (
                <div className="skeleton-card" key={key}><div /><span /><span /></div>
              ))}
            </div>
          )}
          {!error && !loading && visibleProducts.length > 0 && (
            <div className={`product-grid product-grid--shop is-${viewMode}`}>
              {visibleProducts.map((product) => <ProductCard key={product._id} product={product} />)}
            </div>
          )}
          {!loading && !error && !visibleProducts.length && (
            <div className="empty-state">
              <span className="empty-state__icon">✳</span>
              <h2>No finds just yet.</h2>
              <p>Try another search or browse everything in the store.</p>
              <button className="button button--primary" onClick={() => { setSearchParams({}); setPage(1); }}>
                See all products <ArrowRight size={17} />
              </button>
            </div>
          )}
          {!loading && count > 8 && (
            <div className="pagination">
              <span>Showing {Math.min((page - 1) * 8 + 1, count)}–{Math.min(page * 8, count)} of {count} finds</span>
              <div>
                <button disabled={page <= 1} onClick={() => setPage((current) => current - 1)}>Previous</button>
                <span>{page}</span>
                <button disabled={page * 8 >= count} onClick={() => setPage((current) => current + 1)}>Next <ArrowRight size={15} /></button>
              </div>
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
